import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import Badge from '../../atoms/Badge/Badge';
import StatusIndicator from '../../atoms/StatusIndicator/StatusIndicator';

const baseRowStyles = (striped) => ({
  backgroundColor: striped ? alpha('#000000', 0.55) : alpha('#000000', 0.35),
  borderColor: 'transparent',
  textColor: 'rgba(245,246,249,0.92)',
  hoverBackground: alpha('#ffffff', 0.08),
  indicator: null,
});

const STATUS_CONFIG = {
  returning: {
    borderColor: '#4EC2F5',
    hoverBackground: alpha('#4EC2F5', 0.18),
    indicator: {
      label: '↩',
      color: '#4EC2F5',
      fontSize: 18,
      fontWeight: 600,
    },
  },
  confirmed: {
    borderColor: '#1FC760',
    hoverBackground: alpha('#1FC760', 0.2),
    indicator: {
      label: '✓',
      color: '#1FC760',
      fontSize: 18,
      fontWeight: 700,
    },
  },
  locked: {
    backgroundColor: '#FEED66',
    borderColor: '#FEED66',
    textColor: '#1D1B20',
    hoverBackground: '#FFEFA6',
    indicator: {
      label: '✓',
      variant: 'badge',
      color: '#1D1B20',
      backgroundColor: '#FEED66',
      borderColor: '#FCC144',
    },
  },
};

const PlayerRow = ({ name, number, striped, status, cards }) => {
  const config = STATUS_CONFIG[status] ?? {};
  const defaults = baseRowStyles(striped);
  const backgroundColor = config.backgroundColor ?? defaults.backgroundColor;
  const borderColor = config.borderColor ?? defaults.borderColor;
  const textColor = config.textColor ?? defaults.textColor;
  const hoverBackground = config.hoverBackground ?? defaults.hoverBackground;
  const indicator = config.indicator ?? defaults.indicator;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        px: 0.75,
        py: 0.75,
        borderRadius: 1,
        bgcolor: backgroundColor,
        border: `2px solid ${borderColor}`,
        pr: indicator ? 1 : 0.75,
        gap: 0.75,
        cursor: 'pointer',
        transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          bgcolor: hoverBackground,
          boxShadow: indicator ? `0 0 0 1px ${alpha('#ffffff', 0.08)}` : 'none',
        },
      }}
    >
      <Badge>{number}</Badge>
      <Typography
        sx={{
          flexGrow: 1,
          fontSize: 16,
          lineHeight: '22px',
          color: textColor,
          fontFamily: '"Barlow", "Roboto", "Helvetica", Arial, sans-serif',
          fontWeight: 500,
          letterSpacing: '0.01em',
        }}
      >
        {name}
      </Typography>
      {/* Card Badges */}
      {cards && (cards.yellow > 0 || cards.red > 0) && (
        <Stack direction="row" spacing={0.5} alignItems="center">
          {cards.yellow > 0 && (
            <Box
              sx={{
                width: 16,
                height: 20,
                bgcolor: '#FEEC67',
                borderRadius: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                fontWeight: 700,
                color: '#000',
              }}
            >
              {cards.yellow > 1 ? cards.yellow : ''}
            </Box>
          )}
          {cards.red > 0 && (
            <Box
              sx={{
                width: 16,
                height: 20,
                bgcolor: '#D1030D',
                borderRadius: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                fontWeight: 700,
                color: '#FFF',
              }}
            >
              {cards.red > 1 ? cards.red : ''}
            </Box>
          )}
        </Stack>
      )}
      <StatusIndicator indicator={indicator} />
    </Stack>
  );
};

PlayerRow.propTypes = {
  name: PropTypes.string.isRequired,
  number: PropTypes.string,
  striped: PropTypes.bool,
  status: PropTypes.string,
  cards: PropTypes.shape({
    yellow: PropTypes.number,
    red: PropTypes.number,
  }),
};

export default PlayerRow;

